import React, { useEffect, useState } from "react";
import {
  Modal,
  View,
  Text as RNText,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Pressable,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useToast } from "react-native-toast-notifications";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { pushFileToS3 } from "../../../services/s3BucketService";
import {
  Attachment,
  SampleRequestDetail,
} from "../../../models/sampleRequestModel";

interface EditSampleRequestModalProps {
  visible: boolean;
  onClose: () => void;
  request: SampleRequestDetail;
  onSubmit: (data: {
    sampleInfo: Record<string, any>;
    deliveryInfo: Record<string, any>;
  }) => void;
}

interface PendingFile {
  id: string;
  fileName: string;
  fileUri: string;
  fileType: string;
}

type FormErrors = Partial<
  Record<
    | "customer"
    | "dateOfRequest"
    | "itemName"
    | "sampleQtyNeeded"
    | "purpose"
    | "deliveryContactPerson"
    | "deliveryContactNumber"
    | "deliveryAddress",
    string
  >
>;

const PURPOSE_OPTIONS = [
  "New Sample Customer",
  "Depth or material change",
  "Colour matching",
  "Wall thickness change",
];

const SAMPLE_REQUEST_S3_FOLDER = "sample-requests";

const buildUniqueFileName = (fileName: string) =>
  `${Date.now()}_${fileName.replace(/[\s,]+/g, "_")}`;

const parseDDMMYYYY = (value: string): Date | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatDateForInput = (value?: string): string => {
  if (!value) return "";
  if (parseDDMMYYYY(value)) return value.trim();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = parsed.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export const EditSampleRequestModal: React.FC<EditSampleRequestModalProps> = ({
  visible,
  onClose,
  request,
  onSubmit,
}) => {
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const toast = useToast();

  const [customer, setCustomer] = useState(request.sampleInfo.customer);
  const [dateOfRequest, setDateOfRequest] = useState(
    formatDateForInput(request.sampleInfo.dateOfRequest),
  );
  const [itemName, setItemName] = useState(request.sampleInfo.itemName);
  const [versionNumber, setVersionNumber] = useState(
    request.sampleInfo.versionNumber || "",
  );
  const [sampleQtyNeeded, setSampleQtyNeeded] = useState(
    String(request.sampleInfo.sampleQtyNeeded ?? ""),
  );
  const [descriptionOfSample, setDescriptionOfSample] = useState(
    request.sampleInfo.descriptionOfSample || "",
  );
  const [purpose, setPurpose] = useState(request.sampleInfo.purpose || "");
  const [purposeDropdownOpen, setPurposeDropdownOpen] = useState(false);

  const [deliveryContactPerson, setDeliveryContactPerson] = useState(
    request.deliveryInfo.deliveryContactPerson || "",
  );
  const [deliveryContactNumber, setDeliveryContactNumber] = useState(
    request.deliveryInfo.deliveryContactNumber || "",
  );
  const [deliveryAddress, setDeliveryAddress] = useState(
    request.deliveryInfo.deliveryAddress || "",
  );
  const [remarks, setRemarks] = useState(request.deliveryInfo.remarks || "");

  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    request.sampleInfo.attachments || [],
  );
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const clearError = (field: keyof FormErrors) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): { valid: boolean; errors: FormErrors } => {
    const next: FormErrors = {};

    if (!customer.trim()) next.customer = "Customer is required.";
    if (!itemName.trim()) next.itemName = "Item name is required.";

    if (!sampleQtyNeeded.trim()) {
      next.sampleQtyNeeded = "Sample qty needed is required.";
    } else {
      const qty = Number(sampleQtyNeeded);
      if (!Number.isInteger(qty) || qty < 1) {
        next.sampleQtyNeeded = "Enter a whole number of 1 or more.";
      }
    }

    if (!purpose) next.purpose = "Select a purpose.";

    if (dateOfRequest.trim() && !parseDDMMYYYY(dateOfRequest)) {
      next.dateOfRequest = "Use DD/MM/YYYY format.";
    }

    if (!deliveryContactPerson.trim()) {
      next.deliveryContactPerson = "Delivery contact person is required.";
    }
    if (!deliveryContactNumber.trim()) {
      next.deliveryContactNumber = "Delivery contact number is required.";
    }
    if (!deliveryAddress.trim()) {
      next.deliveryAddress = "Delivery address is required.";
    }

    return { valid: Object.keys(next).length === 0, errors: next };
  };

  useEffect(() => {
    if (!visible) return;
    setCustomer(request.sampleInfo.customer);
    setDateOfRequest(formatDateForInput(request.sampleInfo.dateOfRequest));
    setItemName(request.sampleInfo.itemName);
    setVersionNumber(request.sampleInfo.versionNumber || "");
    setSampleQtyNeeded(String(request.sampleInfo.sampleQtyNeeded ?? ""));
    setDescriptionOfSample(request.sampleInfo.descriptionOfSample || "");
    setPurpose(request.sampleInfo.purpose || "");
    setDeliveryContactPerson(request.deliveryInfo.deliveryContactPerson || "");
    setDeliveryContactNumber(request.deliveryInfo.deliveryContactNumber || "");
    setDeliveryAddress(request.deliveryInfo.deliveryAddress || "");
    setRemarks(request.deliveryInfo.remarks || "");
    setExistingAttachments(request.sampleInfo.attachments || []);
    setPendingFiles([]);
    setPurposeDropdownOpen(false);
    setErrors({});
  }, [visible, request._id]);

  const pickFilesWeb = () => {
    if (Platform.OS !== "web") {
      toast.show("File upload isn't wired up for native yet", {
        type: "warning",
      });
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.setAttribute("multiple", "multiple");
    input.accept = "image/*,.pdf,.ai,.dwg,.dxf";
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "0";

    const cleanup = () => {
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.onchange = () => {
      const selected = input.files ? Array.from(input.files) : [];
      if (selected.length === 0) {
        cleanup();
        return;
      }

      let processed = 0;
      selected.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          setPendingFiles((prev) => [
            ...prev,
            {
              id: Math.random().toString(36).substring(2, 15),
              fileName: file.name,
              fileUri: reader.result as string,
              fileType: file.type || "application/octet-stream",
            },
          ]);
          processed += 1;
          if (processed === selected.length) cleanup();
        };
        reader.onerror = () => {
          toast.show(`Could not read ${file.name}`, { type: "danger" });
          processed += 1;
          if (processed === selected.length) cleanup();
        };
        reader.readAsDataURL(file);
      });
    };

    document.body.appendChild(input);
    input.click();
  };

  const removeExistingAttachment = (fileName: string) => {
    setExistingAttachments((prev) =>
      prev.filter((a) => a.fileName !== fileName),
    );
  };

  const removePendingFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async () => {
    const { valid, errors: validationErrors } = validate();
    setErrors(validationErrors);
    if (!valid) {
      toast.show("Please fix the highlighted fields", { type: "warning" });
      return;
    }

    setIsSubmitting(true);
    try {
      const uploaded: Attachment[] = [];
      for (const file of pendingFiles) {
        const uniqueFileName = buildUniqueFileName(file.fileName);
        const ok = await pushFileToS3(
          "POST",
          file.fileUri,
          uniqueFileName,
          file.fileType,
          SAMPLE_REQUEST_S3_FOLDER,
        );
        if (!ok) throw new Error(`Failed to upload ${file.fileName}`);
        uploaded.push({ fileName: uniqueFileName });
      }

      const parsedDate = dateOfRequest.trim()
        ? parseDDMMYYYY(dateOfRequest)
        : null;

      onSubmit({
        sampleInfo: {
          customer: customer.trim(),
          ...(parsedDate ? { dateOfRequest: parsedDate.toISOString() } : {}),
          itemName: itemName.trim(),
          versionNumber,
          sampleQtyNeeded: Number(sampleQtyNeeded),
          descriptionOfSample,
          purpose,
          attachments: [
            ...existingAttachments.map(({ fileName }) => ({ fileName })),
            ...uploaded,
          ],
        },
        deliveryInfo: {
          deliveryContactPerson,
          deliveryContactNumber,
          deliveryAddress,
          remarks,
        },
      });
    } catch (error: any) {
      console.error("Error uploading edit attachments:", error);
      toast.show(
        error?.message || "Failed to upload attachments — changes not saved",
        { type: "danger" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable
          style={styles.modalCard}
          onPress={(e) => e.stopPropagation()}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            <RNText style={styles.title}>Edit Request</RNText>
            <RNText style={styles.subtitle}>
              Update the sample or delivery details for {request.requestId}.
            </RNText>

            <RNText style={styles.sectionHeader}>1. Sample Info</RNText>

            <Field label="Customer" error={errors.customer}>
              <TextInput
                style={[styles.input, errors.customer && styles.inputError]}
                value={customer}
                onChangeText={(t) => {
                  setCustomer(t);
                  clearError("customer");
                }}
                placeholderTextColor={colors.textSecondary}
              />
            </Field>

            <View style={styles.row}>
              <Field
                label="Date of Sample Request"
                flex={1}
                error={errors.dateOfRequest}
              >
                <TextInput
                  style={[
                    styles.input,
                    errors.dateOfRequest && styles.inputError,
                  ]}
                  value={dateOfRequest}
                  onChangeText={(t) => {
                    setDateOfRequest(t);
                    clearError("dateOfRequest");
                  }}
                  placeholder="DD/MM/YYYY"
                  placeholderTextColor={colors.textSecondary}
                />
              </Field>
              <Field label="Item Name" flex={1} error={errors.itemName}>
                <TextInput
                  style={[styles.input, errors.itemName && styles.inputError]}
                  value={itemName}
                  onChangeText={(t) => {
                    setItemName(t);
                    clearError("itemName");
                  }}
                  placeholderTextColor={colors.textSecondary}
                />
              </Field>
            </View>

            <View style={styles.row}>
              <Field label="Version #" flex={1}>
                <TextInput
                  style={styles.input}
                  value={versionNumber}
                  onChangeText={setVersionNumber}
                  placeholderTextColor={colors.textSecondary}
                />
              </Field>
              <Field
                label="Sample Qty Needed"
                flex={1}
                error={errors.sampleQtyNeeded}
              >
                <TextInput
                  style={[
                    styles.input,
                    errors.sampleQtyNeeded && styles.inputError,
                  ]}
                  value={sampleQtyNeeded}
                  onChangeText={(t) => {
                    setSampleQtyNeeded(t);
                    clearError("sampleQtyNeeded");
                  }}
                  keyboardType="numeric"
                  placeholderTextColor={colors.textSecondary}
                />
              </Field>
            </View>

            <Field label="Description of Sample">
              <TextInput
                style={[styles.input, styles.textArea]}
                value={descriptionOfSample}
                onChangeText={setDescriptionOfSample}
                multiline
                numberOfLines={3}
                placeholderTextColor={colors.textSecondary}
              />
            </Field>

            <Field label="Purpose of Sample Request" error={errors.purpose}>
              <TouchableOpacity
                style={[
                  styles.dropdownTrigger,
                  errors.purpose && styles.inputError,
                ]}
                onPress={() => setPurposeDropdownOpen(!purposeDropdownOpen)}
              >
                <RNText
                  style={
                    purpose
                      ? styles.dropdownValueText
                      : styles.dropdownPlaceholderText
                  }
                >
                  {purpose || "Select purpose"}
                </RNText>
                <RNText style={styles.dropdownArrow}>
                  {purposeDropdownOpen ? "▲" : "▼"}
                </RNText>
              </TouchableOpacity>
              {purposeDropdownOpen && (
                <View style={styles.dropdownList}>
                  {PURPOSE_OPTIONS.map((option) => (
                    <TouchableOpacity
                      key={option}
                      style={styles.dropdownOption}
                      onPress={() => {
                        setPurpose(option);
                        setPurposeDropdownOpen(false);
                        clearError("purpose");
                      }}
                    >
                      <RNText style={styles.dropdownOptionText}>
                        {option}
                      </RNText>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </Field>

            <Field label="Image/ Drawing">
              <TouchableOpacity style={styles.uploadBox} onPress={pickFilesWeb}>
                <Ionicons
                  name="cloud-upload-outline"
                  size={18}
                  color={colors.textSecondary}
                  style={{ marginBottom: 4 }}
                />
                <RNText style={styles.uploadBoxText}>
                  Upload a drawing or spec sheet
                </RNText>
              </TouchableOpacity>

              {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
                <View style={{ marginTop: 10, gap: 6 }}>
                  {existingAttachments.map((a) => (
                    <View key={a.fileName} style={styles.fileRow}>
                      <Ionicons
                        name="document-attach-outline"
                        size={14}
                        color={colors.textSecondary}
                      />
                      <RNText
                        style={styles.fileRowText}
                        numberOfLines={1}
                        ellipsizeMode="middle"
                      >
                        {a.fileName}
                      </RNText>
                      <TouchableOpacity
                        onPress={() => removeExistingAttachment(a.fileName)}
                      >
                        <Ionicons
                          name="close-circle"
                          size={16}
                          color={colors.textSecondary}
                        />
                      </TouchableOpacity>
                    </View>
                  ))}
                  {pendingFiles.map((file) => (
                    <View key={file.id} style={styles.fileRow}>
                      <Ionicons
                        name="document-attach-outline"
                        size={14}
                        color={colors.textSecondary}
                      />
                      <RNText
                        style={styles.fileRowText}
                        numberOfLines={1}
                        ellipsizeMode="middle"
                      >
                        {file.fileName}
                      </RNText>
                      <TouchableOpacity
                        onPress={() => removePendingFile(file.id)}
                      >
                        <Ionicons
                          name="close-circle"
                          size={16}
                          color={colors.textSecondary}
                        />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
            </Field>

            <RNText style={styles.sectionHeader}>2. Delivery Info</RNText>

            <View style={styles.row}>
              <Field
                label="Delivery Contact Person"
                flex={1}
                error={errors.deliveryContactPerson}
              >
                <TextInput
                  style={[
                    styles.input,
                    errors.deliveryContactPerson && styles.inputError,
                  ]}
                  value={deliveryContactPerson}
                  onChangeText={(t) => {
                    setDeliveryContactPerson(t);
                    clearError("deliveryContactPerson");
                  }}
                  placeholderTextColor={colors.textSecondary}
                />
              </Field>
              <Field
                label="Delivery Contact No."
                flex={1}
                error={errors.deliveryContactNumber}
              >
                <TextInput
                  style={[
                    styles.input,
                    errors.deliveryContactNumber && styles.inputError,
                  ]}
                  value={deliveryContactNumber}
                  onChangeText={(t) => {
                    setDeliveryContactNumber(t);
                    clearError("deliveryContactNumber");
                  }}
                  keyboardType="phone-pad"
                  placeholderTextColor={colors.textSecondary}
                />
              </Field>
            </View>

            <Field label="Delivery Address" error={errors.deliveryAddress}>
              <TextInput
                style={[
                  styles.input,
                  errors.deliveryAddress && styles.inputError,
                ]}
                value={deliveryAddress}
                onChangeText={(t) => {
                  setDeliveryAddress(t);
                  clearError("deliveryAddress");
                }}
                placeholderTextColor={colors.textSecondary}
              />
            </Field>

            <Field label="Remarks">
              <TextInput
                style={[styles.input, styles.textArea]}
                value={remarks}
                onChangeText={setRemarks}
                multiline
                numberOfLines={3}
                placeholderTextColor={colors.textSecondary}
              />
            </Field>

            <View style={styles.footerRow}>
              <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
                <RNText style={styles.cancelButtonText}>Cancel</RNText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.submitButton,
                  isSubmitting && styles.submitButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={isSubmitting}
              >
                <RNText style={styles.submitButtonText}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </RNText>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const Field = ({
  label,
  children,
  flex,
  error,
}: {
  label: string;
  children: React.ReactNode;
  flex?: number;
  error?: string;
}) => {
  const { colors, isDark } = useAppTheme();
  return (
    <View style={{ flex, marginBottom: 16 }}>
      <RNText
        style={{
          fontSize: 13,
          fontWeight: "500",
          color: isDark ? colors.textSecondary : "#374151",
          marginBottom: 6,
        }}
      >
        {label}
      </RNText>
      {children}
      {!!error && (
        <RNText
          style={{
            fontSize: 12,
            color: "#DC2626",
            marginTop: 4,
          }}
        >
          {error}
        </RNText>
      )}
    </View>
  );
};

function createStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.5)",
      justifyContent: "center",
      alignItems: "center",
    },
    modalCard: {
      width: 520,
      maxHeight: "88%",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      padding: 28,
    },
    title: {
      fontSize: 20,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
      marginBottom: 4,
    },
    subtitle: {
      fontSize: 13,
      color: isDark ? colors.textSecondary : "#64748B",
      marginBottom: 20,
    },
    sectionHeader: {
      fontSize: 13,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
      backgroundColor: isDark ? colors.background : "#EFF6FF",
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 6,
      marginBottom: 16,
      marginTop: 4,
    },
    row: {
      flexDirection: "row",
      gap: 16,
    },
    input: {
      backgroundColor: isDark ? colors.background : "#F8FAFC",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },
    inputError: {
      borderColor: "#DC2626",
    },
    textArea: {
      minHeight: 72,
      textAlignVertical: "top",
    },
    dropdownTrigger: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: isDark ? colors.background : "#F8FAFC",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    dropdownValueText: {
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },
    dropdownPlaceholderText: {
      fontSize: 13,
      color: colors.textSecondary,
    },
    dropdownArrow: {
      fontSize: 10,
      color: colors.textSecondary,
    },
    dropdownList: {
      marginTop: 4,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      overflow: "hidden" as any,
    },
    dropdownOption: {
      paddingVertical: 10,
      paddingHorizontal: 12,
    },
    dropdownOptionText: {
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },
    uploadBox: {
      backgroundColor: isDark ? colors.background : "#F8FAFC",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      borderStyle: "dashed",
      paddingVertical: 14,
      alignItems: "center",
    },
    uploadBoxText: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    fileRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      backgroundColor: isDark ? colors.background : "#F1F5F9",
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    fileRowText: {
      flex: 1,
      fontSize: 12,
      color: isDark ? colors.text : "#334155",
    },
    footerRow: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 8,
    },
    cancelButton: {
      paddingHorizontal: 18,
      paddingVertical: 13,
      borderRadius: 8,
      backgroundColor: isDark ? colors.background : "#E2E8F0",
    },
    cancelButtonText: {
      fontSize: 14,
      fontWeight: "600",
      color: isDark ? colors.text : "#334155",
    },
    submitButton: {
      backgroundColor: isDark ? colors.primary : "#2F95DC",
      borderRadius: 8,
      paddingHorizontal: 24,
      paddingVertical: 13,
      alignItems: "center",
    },
    submitButtonDisabled: {
      opacity: 0.6,
    },
    submitButtonText: {
      color: "#FFFFFF",
      fontSize: 14,
      fontWeight: "700",
    },
  });
}
